import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FireflyMobileModalComponent, ModalContent } from '../../../../modal';
import type {
  ChartDataEntry,
  ChartDataSeries,
  FireflyChart,
  LineChartMobilePopoverContext
} from '../../../common/models/common-chart-models';

@Component({
  selector: 'f-line-chart-mobile-popover',
  imports: [NgTemplateOutlet],
  template: `
    <div style="height: 300px">
      <ng-template #chartContainer></ng-template>
    </div>
    <hr />
    <div class="overflow-auto">
      <ng-container
        *ngTemplateOutlet="$any(context.template); context: { $implicit: context.popoverData }"
      ></ng-container>
    </div>
  `
})
export class FireflyLineChartMobilePopoverComponent implements OnInit, ModalContent {
  chart!: ComponentRef<FireflyChart>;
  context!: LineChartMobilePopoverContext;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalContainer = inject(FireflyMobileModalComponent, { skipSelf: true });
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('chartContainer', { static: true, read: ViewContainerRef })
  private chartContainer!: ViewContainerRef;

  ngOnInit() {
    this.modalContainer.toggleAcceptBtnVisibility(false);

    if (!this.context.component) return;

    this.setUpChartInstance();
  }

  private setUpChartInstance() {
    this.chart = this.chartContainer.createComponent(this.context.component);

    this.applyBaseSettings();
    this.applyBoilerplateSettings();
    this.applyLineSettings();
    this.applyLegendSettings();
    this.setInitialPointerState();
    this.subscribeToLineClicks();
    this.cdr.detectChanges();
  }

  private applyBaseSettings(): void {
    this.chart.instance.fitContainer = true;
    // Use setInput() instead of direct assignment so Angular tracks these as input
    // changes, triggers ngOnChanges, and buildComputedData() runs with both data and
    // lineData set — merging the dashed series into computedData for rendering.
    this.chart.setInput('data', this.context.chartData);
    // Pass the dashed dataset separately so the mobile chart computes solidSeriesCount
    // and lineYScale correctly, matching the behaviour of the parent chart.
    if (this.context.dashedSeriesData) {
      this.chart.setInput('dashedSeriesData', this.context.dashedSeriesData);
    }
    this.chart.instance.view = { width: window.innerWidth, height: 300 };
    this.chart.instance.interactions = { click: true, hover: true, preventMobile: true };
    this.chart.instance.condensedAxisOnMobile = false;
    this.chart.instance.animation = false;
  }

  private applyBoilerplateSettings(): void {
    this.chart.instance.showBorders = this.context.chartBoilerplate.showBorders;
    this.chart.instance.showYAxisDomain = this.context.chartBoilerplate.showYAxisDomain;
    this.chart.instance.showXAxisDomain = this.context.chartBoilerplate.showXAxisDomain;
    this.chart.instance.showXAxisTicksLabels = this.context.chartBoilerplate.showXAxisTicksLabels;
    this.chart.instance.showYAxisTicksLabels = this.context.chartBoilerplate.showYAxisTicksLabels;
    this.chart.instance.showXAxisGridlines = this.context.chartBoilerplate.showXAxisGridlines;
    this.chart.instance.showYAxisGridlines = this.context.chartBoilerplate.showYAxisGridlines;
    this.chart.instance.leftAxisLabelText = this.context.chartBoilerplate.leftAxisLabelText;
    this.chart.instance.xAxisTickFormatting = this.context.chartBoilerplate.xAxisTickFormatting;
    this.chart.instance.yAxisTickFormatting = this.context.chartBoilerplate.yAxisTickFormatting;
    this.chart.instance.integerYAxis = this.context.chartBoilerplate.integerYAxis;
  }

  private applyLineSettings(): void {
    this.chart.instance.lineClasses = this.context.lineClasses;
    this.chart.instance.lineWidth = this.context.lineWidth;
    this.chart.instance.circleRadius = this.context.circleRadius;
    this.chart.instance.showPointerLine = true;
  }

  private applyLegendSettings(): void {
    this.chart.instance.showLegend = Boolean(this.context.showLegend);
    if (!this.context.showLegend || !this.context.legendOptions) return;
    this.chart.instance.legendOptions = { ...this.context.legendOptions };
    this.chart.instance.legendOptions.legendContentAlignment = 'center';
    this.chart.instance.legendOptions.legendWrappingLinesCount = 10;
    this.chart.instance.legendOptions.legendPosition = 'bottom';
  }

  private setInitialPointerState(attempt = 0): void {
    // Defer until the next frame so layout/scales are computed before positioning the pointer.
    window.requestAnimationFrame(() => {
      const chartInstance = this.chart?.instance as FireflyChart & {
        activeDataPointId?: number;
        pointerXCoordinate?: number;
        dimensions?: { width: number; height: number };
      };
      const targetIndex = this.context.activeDataPointId ?? 0;
      const targetPoint = this.context.chartData?.[targetIndex];

      // Guard against missing chart instance or data while the modal mounts.
      if (!chartInstance || !targetPoint) return;
      const hasDimensions = !!chartInstance.dimensions?.width && !!chartInstance.dimensions?.height;
      // Wait until xScale/dimensions are ready; otherwise pointer math is wrong.
      if (!chartInstance.xScale || !hasDimensions) {
        if (attempt < 5) this.setInitialPointerState(attempt + 1);
        return;
      }

      // Pass the band left edge — f-line compares nearestXPointer against left edges internally
      // and computes cx = xScale(name) + bandwidth/2 independently for the DOM circle.
      const x = chartInstance.xScale(targetPoint.name) ?? 0;

      // Drive the line component’s pointer positioning with the selected datapoint.
      chartInstance.activeDataPointId = targetIndex;
      chartInstance.pointerXCoordinate = x;
      this.chart.changeDetectorRef.detectChanges();

      // Re-run a few frames to handle late dimension updates.
      if (attempt < 5) this.setInitialPointerState(attempt + 1);
    });
  }

  private subscribeToLineClicks(): void {
    if (!this.chart.instance.lineClick) return;
    const sub = this.chart.instance.lineClick.subscribe(({ data, index }) => {
      this.context.popoverData = this.buildPopoverData(data, index);
      this.cdr.detectChanges();
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  private buildPopoverData(entry: ChartDataSeries, _index: number): unknown {
    // Preserve the cssClass from the initial popoverData (which has merged line + legend classes)
    // and update only the date name and series values for the newly selected data point.
    const existingData = this.context.popoverData as { name: string; series: ChartDataEntry[] } | null;
    const newSeries = entry.series as ChartDataEntry[];

    if (!existingData?.series?.length) {
      return { name: entry.name, series: newSeries };
    }

    // Look up dashed series for this x-axis point from context.lineData.
    const dashedEntry = (this.context.dashedSeriesData ?? []).find(d => d.name === entry.name);
    const dashedSeries = (dashedEntry?.series ?? []) as ChartDataEntry[];

    // Build a name→value map across both solid and dashed sources.
    const valueByName = new Map<string, number | null>();
    newSeries.forEach(s => valueByName.set(s.name as string, s.value as number | null));
    dashedSeries.forEach(s => valueByName.set(s.name as string, s.value as number | null));

    return {
      name: entry.name,
      series: existingData.series.map(s => {
        const newValue = valueByName.get(s.name as string);
        return newValue !== undefined ? { ...s, value: newValue } : s;
      })
    };
  }
}
